import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaCheck, FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import protectionProgramService from "../../../../../services/web/protection-program-service.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {FaXmark} from "react-icons/fa6";

const initialFilters = {
    name: "",
    protectiveMeasure: "all"
}

const ProtectionProgramList = () => {
    const navigate = navigateHook();

    const [filter, setFilter] = useState([]);
    const [filterValues, setFilterValues] = useState(initialFilters);

    const {data} = useQuery({
        queryFn: () => protectionProgramService.list(filter),
        queryKey: ["list", {filter}]
    });

    const [columnDefs,] = useState([
        { field: "name", headerName: "Nome", sort: "asc", flex: 1, comparator: aggridUtils.nullLastComparator},
        {
            field: "protective_measure",
            headerName: "Medida protetiva",
            width: 165,
            cellRenderer: ({value}) => {
                return (
                    <div className={"flex items-center justify-center h-full"}>
                        {
                            value ? <FaCheck color={"#219453"}/> : <FaXmark color={"#ed5565"}/>
                        }
                    </div>
                )
            },
        },
    ])

    const gridRef = useRef(undefined);

    const apply = useCallback(() => {
        setFilter(Object.keys(filterValues).map(field => ({field, type: "like", value: filterValues[field]})));
    }, [filterValues])

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, [])

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Enter") {
                apply();
            }
            if (event.key === "Escape") {
                clear();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [apply, clear]);

    const onCellClicked = ({data}) => {
        navigate(`/sos/protection-program/form/${data.id}`)
    };

    return (
        <Template>
            <Template.Header>
                <Template.Title>Programa de proteção</Template.Title>

                <Button target={"primary"} onClick={() => navigate("/sos/protection-program/form")}>
                    <FaPlus/> Adicionar programa
                </Button>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"grid grid-cols-12 gap-4"}>
                        <InputForm
                            classNameWrapper={"col-span-6"}
                            id={"name"}
                            label={"Nome"}
                            name={"name"}
                            value={filterValues.name}
                            onChange={({target: {value}}) => setFilterValues(prev => ({...prev, name: value}))}
                        />

                        <div className="col-span-6 flex justify-between gap-4">
                            <SelectComponent
                                classNameWrapper={"w-full"}
                                value={filterValues.protectiveMeasure}
                                options={[{value:"all", label: "Todos"}, {value:"true", label: "Sim"}, {value:"false", label: "Não"}]}
                                label={"Medida protetiva"}
                                onChange={(value) => setFilterValues(prev => ({...prev, protectiveMeasure: value?.value}))}
                                isClearable={false}
                            />

                            <div className={"flex gap-2"}>
                                <Button target={"default"} onClick={clear}>
                                    <FaEraser />
                                </Button>
                                <Button onClick={apply}>
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div className={"h-[calc(100vh-153px)] aggrid-row-pointer"}>
                            <AgGridReact
                                ref={gridRef}
                                rowHeight={32}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                onCellClicked={onCellClicked}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default ProtectionProgramList;